import { useEffect, useRef } from "react";
import { api } from "./api";

/**
 * useBookingIntent — heartbeat a customer's in-progress booking to the
 * server every 10s while they're filling out the form. The staff-side
 * calendar reads these intents and renders soft "ghost" pills so two
 * managers (or two customers) never accidentally double-book the same
 * vehicle on the same date.
 *
 * Fires POST /api/booking-intent/heartbeat on state change and every 10s
 * afterwards. Fires DELETE on unmount so ghosts disappear the instant a
 * customer leaves the form or completes the booking.
 *
 * Pass `enabled=false` (e.g. once booking is confirmed) to stop pinging
 * without unmounting.
 */
export function useBookingIntent({ enabled = true, vehicleId, vehicleName, bookingType, startAt, endAt, stage }) {
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    // Stable per-tab session id — regenerated on hard refresh
    sessionIdRef.current = `intent_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
  }
  useEffect(() => {
    if (!enabled || !vehicleId || !startAt) return undefined;
    const sessionId = sessionIdRef.current;
    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      api.post("/booking-intent/heartbeat", {
        session_id: sessionId,
        vehicle_id: vehicleId,
        vehicle_name: vehicleName || null,
        booking_type: bookingType || "rental",
        start_at: startAt,
        end_at: endAt || null,
        stage: stage || null,
      }).catch(() => {});
    };
    ping();
    const interval = setInterval(ping, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      // Best-effort intent cleanup — fires-and-forgets so it survives navigation.
      api.delete(`/booking-intent/heartbeat/${sessionId}`).catch(() => {});
    };
  }, [enabled, vehicleId, vehicleName, bookingType, startAt, endAt, stage]);
}
